import { type ComputedRef, type Ref, ref, computed } from "vue";

import apiSkobo from "@/helpers/api/apiSkobo";
import ConfidentialFolder from "@/models/People/ConfidentialFolder.js";
import DriveItem from "@/models/Documents/DriveItem";

import { useSettingsStore } from "@/stores/settings";
import { usePeopleStore } from "@/stores/people";

interface ConfidentialFolderLike {
  driveId?: string;
  itemId?: string;
  parentReference?: { driveId?: string; id?: string };
  id?: string;
  userId?: string;
  sectionId?: string;
  sectionDisplayName?: string;
  extSysApiUserId?: string | null;
  [key: string]: unknown;
}

interface CombiDriveFolderLike {
  driveId: string;
  itemId: string;
  [key: string]: unknown;
}

interface ConfidentialDocumentsComposableDependencies {
  driveItem: ComputedRef<(driveId: string, itemId: string) => DriveItem | null>;
  calculateDriveItemsHierarchy: (driveId: string, itemId: string) => Promise<CombiDriveFolderLike[]>;
}

export const useConfidentialDocumentsComposable = (dependencies: ConfidentialDocumentsComposableDependencies) => {
  const confidentialDocumentsObs: Ref<ConfidentialFolderLike[]> = ref([]);
  const sectionConfidentialFoldersMap: Ref<Record<string, ConfidentialFolderLike[]>> = ref({});
  const specialItemTypesMap: Ref<Record<string, Record<string, string>>> = ref({});
  const specialItemsForAppConfidentialMap: Ref<Record<string, boolean>> = ref({});

  const specialItemTypeForDriveItem = computed(() => (driveId: string, itemId: string) => {
    const settingsStore = useSettingsStore();
    return specialItemTypesMap.value[driveId]
      ? specialItemTypesMap.value[driveId][itemId]
      : (
          settingsStore.templateSpecialItems as Array<{ driveId: string; itemId: string; specialItemType: string }>
        ).find(item => item.driveId === driveId && item.itemId === itemId)?.specialItemType;
  });

  const specialItemObjectForDriveItem = computed(() => (driveId: string, itemId: string) => {
    const settingsStore = useSettingsStore();
    const specialItemType = specialItemTypeForDriveItem.value(driveId, itemId);
    if (!specialItemType) {
      return null;
    }
    return (settingsStore.templateSpecialItems as Array<{ specialItemType: string; [key: string]: unknown }>).find(
      item => item.specialItemType == specialItemType
    );
  });

  const driveItemWithNctMeta = computed(() => (driveId: string, itemId: string) => {
    const driveItem = dependencies.driveItem.value(driveId, itemId) as (DriveItem & { error?: unknown }) | null;
    if (!driveItem) {
      return null;
    }
    if (driveItem.error) {
      return driveItem;
    }
    driveItem.nctSpecialItem = specialItemObjectForDriveItem.value(driveId, itemId);
    driveItem.childrenList?.forEach(
      (child: DriveItem) =>
        (child.nctSpecialItem = specialItemObjectForDriveItem.value(child.parentReference.driveId, child.id))
    );
    return driveItem;
  });

  const confidentialFolderForPeopleInSection = computed(() => (sectionId: string) => {
    return (
      sectionConfidentialFoldersMap.value[sectionId]?.filter((folder: ConfidentialFolderLike) => !!folder.userId) || []
    );
  });

  const confidentialFolderForSectionInSection = computed(() => (sectionId: string) => {
    return (
      sectionConfidentialFoldersMap.value[sectionId]?.filter((folder: ConfidentialFolderLike) => !!folder.sectionId) ||
      []
    );
  });

  const confidentialFoldersForPersonInSection = computed(() => (sectionId: string, userId: string) => {
    if (sectionConfidentialFoldersMap.value[sectionId]) {
      return sectionConfidentialFoldersMap.value[sectionId].find(
        (folder: ConfidentialFolderLike) => folder.userId === userId
      );
    }
    return null;
  });

  const confidentialFolderFromId = computed(() => (driveId: string, itemId: string) => {
    for (const sectionKey in sectionConfidentialFoldersMap.value) {
      const userFolder = sectionConfidentialFoldersMap.value[sectionKey].find(
        (folder: ConfidentialFolderLike) =>
          folder.parentReference && folder.parentReference.driveId === driveId && folder.id === itemId
      );
      if (userFolder) {
        return userFolder;
      }
    }
    const fromObs = confidentialDocumentsObs.value.find(
      (folder: ConfidentialFolderLike) =>
        folder.parentReference && folder.parentReference.driveId === driveId && folder.id === itemId
    );
    if (fromObs) {
      return fromObs;
    }
    return null;
  });

  const isDriveConfidentialFolder = computed(() => (driveId: string) => {
    for (const sectionKey in sectionConfidentialFoldersMap.value) {
      return sectionConfidentialFoldersMap.value[sectionKey].some(
        (folder: ConfidentialFolderLike) => folder.driveId && folder.driveId === driveId
      );
    }
    return false;
  });

  const confidentialDocumentsObsSections = computed(() => {
    const settingsStore = useSettingsStore();
    const sectionArray = confidentialDocumentsObs.value.map(
      (obs: ConfidentialFolderLike) => obs.sectionDisplayName as string | undefined
    );
    return [...new Set(sectionArray)].sort((first, second) =>
      (first ?? "").localeCompare(second ?? "", settingsStore.language as string | undefined)
    );
  });

  const confidentialDocumentsObsForSection = computed(() => (sectionDisplayName: string) => {
    return confidentialDocumentsObs.value.filter(
      (obs: ConfidentialFolderLike) => obs.sectionDisplayName === sectionDisplayName
    );
  });

  const confidentialDocumentsObsForPerson = computed(() => (personId: string) => {
    return confidentialDocumentsObs.value.find((obs: ConfidentialFolderLike) => obs.userId === personId);
  });

  const reloadConfidentialFoldersForSection = async (sectionId: string) => {
    const peopleStore = usePeopleStore();
    const promises: Array<Promise<unknown>> = [];
    promises.push(apiSkobo.get(`/appconfidentialfolder/section/${sectionId}`));
    promises.push(peopleStore.loadAllPeopleOnInstitution());

    const response = (await Promise.all(promises)) as [{ data: ConfidentialFolderLike[] }, unknown];

    const confidentialFolders = response[0].data.map((folder: ConfidentialFolderLike) => {
      const folderModel = new ConfidentialFolder(folder);
      if (!folderModel.extSysApiUserId && folderModel.userId) {
        const person = peopleStore.fromId(folderModel.userId);
        folderModel.extSysApiUserId = person ? person.extSysApiUserId : null;
      }
      return folderModel;
    });

    sectionConfidentialFoldersMap.value[sectionId] = confidentialFolders;
  };

  const loadConfidentialFoldersForSection = async (sectionId: string) => {
    if (sectionConfidentialFoldersMap.value[sectionId]) {
      return;
    }
    await reloadConfidentialFoldersForSection(sectionId);
  };

  const reloadConfidentialDocumentsObs = async () => {
    const response = await apiSkobo.get("/appconfidentialfolder/obs/list");
    confidentialDocumentsObs.value = response.data.map(
      (folder: ConfidentialFolderLike) => new ConfidentialFolder(folder)
    );
  };

  const loadConfidentialDocumentsObs = async () => {
    if (confidentialDocumentsObs.value.length === 0) {
      await reloadConfidentialDocumentsObs();
    }
  };

  const grantAccessToConfidentialDocumentsObs = async (confidentialId: string, sectionId: string) => {
    try {
      await apiSkobo.post(`/appconfidentialfolder/${confidentialId}/grant`, { sectionId });
      return true;
    } catch {
      return false;
    }
  };

  const verifyConfidentialFolder = async (confidentialFolderId: string, sectionId: string) => {
    await apiSkobo.post(`/appconfidentialfolder/obs/${confidentialFolderId}/verified`);
    await reloadConfidentialFoldersForSection(sectionId);
  };

  const archiveConfidentialFolder = async (confidentialFolderId: string, targetId: string) => {
    let body: { targetId: string } | undefined;
    if (targetId) {
      body = { targetId };
    }
    await apiSkobo.post(`/appconfidentialfolder/obs/${confidentialFolderId}/archive`, body);
    await reloadConfidentialDocumentsObs();
  };

  const addSpecialItem = async (
    confidentialId: string,
    driveId: string,
    itemId: string,
    specialItemType: string,
    persist = true
  ) => {
    const specialItem = { confidentialId, driveId, itemId, itemType: specialItemType };

    let response: { status?: number } | undefined;
    if (persist) {
      response = await apiSkobo.post("/appconfidentialfolder/specialitems", specialItem);
    }

    if ((response && response.status == 200) || !persist) {
      const clone = specialItemTypesMap.value;
      if (!clone[driveId]) {
        clone[driveId] = {};
      }
      clone[driveId][itemId] = specialItemType;

      specialItemTypesMap.value = clone;
      specialItemsForAppConfidentialMap.value[confidentialId] = true;
    }
  };

  const reloadSpecialItems = async (appConfidentialId: string) => {
    const response = await apiSkobo.get(`/appconfidentialfolder/specialitems/${appConfidentialId}`);
    if (response.status === 200) {
      const clone = specialItemTypesMap.value;
      (response.data as Array<{ driveId: string; itemId: string; itemType: string }>).forEach(item => {
        if (!clone[item.driveId]) {
          clone[item.driveId] = {};
        }
        clone[item.driveId][item.itemId] = item.itemType;
      });
      specialItemTypesMap.value = clone;
      specialItemsForAppConfidentialMap.value[appConfidentialId] = true;
    }
  };

  const loadSpecialItems = async (appConfidentialId: string) => {
    if (!specialItemsForAppConfidentialMap.value[appConfidentialId]) {
      await reloadSpecialItems(appConfidentialId);
    }
  };

  const calculateSpecialItemType = async (driveItem: DriveItem) => {
    const testSpecialItem = specialItemTypeForDriveItem.value(driveItem.parentReference.driveId, driveItem.id);
    if (testSpecialItem) {
      return testSpecialItem;
    }
    if (driveItem.name === "root") {
      return undefined;
    }
    const hierarchyAbove = await dependencies.calculateDriveItemsHierarchy(
      driveItem.parentReference.driveId,
      driveItem.parentReference.id!
    );
    let specialItemTypeForParent: string | undefined;
    for (let i = 0; i < hierarchyAbove.length; i++) {
      if (specialItemTypeForParent) {
        if (!specialItemTypesMap.value[driveItem.parentReference.driveId]) {
          specialItemTypesMap.value[driveItem.parentReference.driveId] = {};
        }
        specialItemTypesMap.value[driveItem.parentReference.driveId][driveItem.id] = specialItemTypeForParent;
      } else {
        specialItemTypeForParent = specialItemTypeForDriveItem.value(
          hierarchyAbove[i].driveId,
          hierarchyAbove[i].itemId
        );
      }
    }
    return specialItemTypeForParent;
  };

  return {
    confidentialDocumentsObs,
    sectionConfidentialFoldersMap,
    specialItemTypesMap,
    specialItemsForAppConfidentialMap,
    specialItemTypeForDriveItem,
    specialItemObjectForDriveItem,
    driveItemWithNctMeta,
    confidentialFolderForPeopleInSection,
    confidentialFolderForSectionInSection,
    confidentialFoldersForPersonInSection,
    confidentialFolderFromId,
    isDriveConfidentialFolder,
    confidentialDocumentsObsSections,
    confidentialDocumentsObsForSection,
    confidentialDocumentsObsForPerson,
    loadConfidentialFoldersForSection,
    reloadConfidentialFoldersForSection,
    loadConfidentialDocumentsObs,
    reloadConfidentialDocumentsObs,
    grantAccessToConfidentialDocumentsObs,
    verifyConfidentialFolder,
    archiveConfidentialFolder,
    addSpecialItem,
    loadSpecialItems,
    reloadSpecialItems,
    calculateSpecialItemType
  };
};
